/**
 * Geolocation utility for web builds
 * Spoofs user location for development/testing purposes when EXPO_PUBLIC_SPOOFED_LATITUDE
 * and EXPO_PUBLIC_SPOOFED_LONGITUDE environment variables are set
 */

// Get spoofed location coordinates from environment variables
const spoofedLatitude = process.env.EXPO_PUBLIC_SPOOFED_LATITUDE;
const spoofedLongitude = process.env.EXPO_PUBLIC_SPOOFED_LONGITUDE;

// Check if both latitude and longitude are provided to enable spoofing
const shouldSpoofLocation = spoofedLatitude !== undefined && spoofedLongitude !== undefined;

/**
 * Wrapper for navigator.geolocation.getCurrentPosition that returns spoofed location for web builds
 * when EXPO_PUBLIC_SPOOFED_LATITUDE and EXPO_PUBLIC_SPOOFED_LONGITUDE are set,
 * otherwise uses real geolocation
 * @param {Function} successCallback - Success callback function
 * @param {Function} errorCallback - Error callback function (optional)
 * @param {Object} options - Geolocation options (optional)
 */
export const getCurrentPosition = (successCallback, errorCallback, options) => {
    // If both latitude and longitude environment variables are set, use spoofed location
    if (shouldSpoofLocation) {
        // Parse the environment variables as numbers
        const latitude = parseFloat(spoofedLatitude);
        const longitude = parseFloat(spoofedLongitude);

        // Validate that the parsed values are valid numbers
        if (isNaN(latitude) || isNaN(longitude)) {
            console.warn('Invalid spoofed location coordinates. Falling back to real geolocation.');
            // Fall back to real geolocation if parsing fails
            if (typeof navigator !== 'undefined' && navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(successCallback, errorCallback, options);
            } else if (errorCallback) {
                errorCallback(new Error('Geolocation not supported'));
            }
            return;
        }

        // Create a spoofed position object matching the GeolocationPosition interface
        const spoofedPosition = {
            coords: {
                latitude: latitude,
                longitude: longitude,
                altitude: null,
                accuracy: 10, // Simulated accuracy in meters
                altitudeAccuracy: null,
                heading: null,
                speed: null,
            },
            timestamp: Date.now(),
        };

        // Call success callback with spoofed position
        // Use setTimeout to simulate async behavior like real geolocation
        setTimeout(() => {
            if (successCallback) {
                successCallback(spoofedPosition);
            }
        }, 0);
    } else {
        // Use real geolocation API
        if (typeof navigator !== 'undefined' && navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(successCallback, errorCallback, options);
        } else if (errorCallback) {
            errorCallback(new Error('Geolocation not supported'));
        }
    }
};

/**
 * Check if geolocation is available
 * @returns {boolean} True if geolocation is available
 */
export const isGeolocationAvailable = () => {
    return typeof window !== 'undefined' && window.navigator !== undefined;
};

